/**
 * Mijn-indeling-cel (Epic C-5 — Mijn indeling & navigatie, Story C-5.1, UX-C6)
 *
 * Read-only rendering van één dagdeel voor "Mijn indeling": exact dezelfde
 * celweergave als C1/C-3.3 (`PlanningCell.tsx`'s `DagdeelContent`) — taak-
 * pasteltint + naam, Vrij/ziek/feestdag/niet-ingeroosterd in de bestaande
 * aanwezigheids-taal (`aanwezigheidVisuals.ts`, geen kopie/nieuwe kleuren).
 *
 * Bewust GEEN klik-handler, GEEN hover-ring, GEEN conflict-/kwalificatie-
 * icoon (die velden bestaan niet eens in `MyAssignmentDagdeelEntry` — de
 * `/me`-respons laat ze server-side weg, AD-16) — dit is puur een `<div>`,
 * geen `<button>`.
 */

import {
  VRIJ_COLOR,
  HOLIDAY_BG,
  HOLIDAY_BORDER,
  HOLIDAY_COLOR,
  ZIEK_STRIPE_LIGHT,
  ZIEK_STRIPE_DARK,
  ZIEK_TEXT,
  KANTOOR_BG,
  KANTOOR_ACCENT,
  KANTOOR_TEXT,
  THUIS_BG,
  THUIS_ACCENT,
  THUIS_TEXT,
  pastelTint,
  readableInk,
  LEAVE_PASTEL_AMOUNT,
  statusLabel,
} from '../../aanwezigheid/components/aanwezigheidVisuals';
import type { MyAssignmentDagdeelEntry } from '@functieloonhuis/shared';

function MijnIndelingHalfContent({ entry, holidayName }: { entry: MyAssignmentDagdeelEntry; holidayName: string | null }) {
  if (entry.assignment) {
    const taskColor = entry.assignment.taskColor ?? '#334155';
    const bg = pastelTint(taskColor, LEAVE_PASTEL_AMOUNT);
    const ink = readableInk(taskColor);
    return (
      <div className="flex items-center justify-center h-full px-1 overflow-hidden" style={{ backgroundColor: bg, boxShadow: `inset 3px 0 0 ${taskColor}` }}>
        <span className="text-[9px] truncate" style={{ color: ink, fontWeight: 700 }}>
          {entry.assignment.taskName}
        </span>
      </div>
    );
  }

  switch (entry.status) {
    case 'feestdag':
      return (
        <div className="flex items-center justify-center gap-1 h-full px-1" style={{ backgroundColor: HOLIDAY_BG, borderColor: HOLIDAY_BORDER }}>
          <span className="text-[9px] font-medium truncate" style={{ color: HOLIDAY_COLOR }}>
            {holidayName ?? 'Feestdag'}
          </span>
        </div>
      );
    case 'ziek':
      return (
        <div
          className="flex items-center justify-center gap-1 h-full px-1"
          style={{ backgroundImage: `repeating-linear-gradient(45deg, ${ZIEK_STRIPE_LIGHT}, ${ZIEK_STRIPE_LIGHT} 4px, ${ZIEK_STRIPE_DARK} 4px, ${ZIEK_STRIPE_DARK} 8px)` }}
        >
          <span className="text-[9px] font-medium truncate" style={{ color: ZIEK_TEXT }}>
            Ziek
          </span>
        </div>
      );
    case 'weekend':
      return (
        <div className="flex items-center justify-center h-full bg-[#F3F4F6]">
          <span className="text-[9px] text-gray-400">Weekend</span>
        </div>
      );
    case 'niet_werkbaar_patroon':
      // Bewust leeg wit vak — geen arcering, geen zichtbaar label (exact
      // aanwezigheidskalender-precedent, zelfde als PlanningCell/AanwezigheidCell).
      return <div className="h-full bg-white" />;
    case 'verlof_goedgekeurd': {
      const bg = pastelTint(VRIJ_COLOR, LEAVE_PASTEL_AMOUNT);
      const ink = readableInk(VRIJ_COLOR);
      return (
        <div className="flex items-center justify-center h-full px-1" style={{ backgroundColor: bg }}>
          <span className="text-[9px] truncate" style={{ color: ink, fontWeight: 500 }}>
            Vrij
          </span>
        </div>
      );
    }
    case 'verlof_in_afwachting':
    case 'werkt': {
      const isKantoor = entry.location !== 'thuis';
      const bg = isKantoor ? KANTOOR_BG : THUIS_BG;
      const accent = isKantoor ? KANTOOR_ACCENT : THUIS_ACCENT;
      const textColor = isKantoor ? KANTOOR_TEXT : THUIS_TEXT;
      return (
        <div className="flex items-center justify-center h-full px-1 overflow-hidden" style={{ backgroundColor: bg, boxShadow: `inset 3px 0 0 ${accent}` }}>
          <span className="text-[9px] truncate" style={{ color: textColor, fontWeight: 700 }}>
            {isKantoor ? 'Kantoor' : 'Thuis'}
          </span>
        </div>
      );
    }
    default: {
      const _exhaustiveCheck: never = entry.status;
      throw new Error(`MijnIndelingCell: onbekende dagdeel-status "${String(_exhaustiveCheck)}"`);
    }
  }
}

function buildAriaLabel(dagLabel: string, dagdeelLabel: string, entry: MyAssignmentDagdeelEntry): string {
  return `${dagLabel} ${dagdeelLabel}: ${entry.assignment ? entry.assignment.taskName : statusLabel(entry.status)}`;
}

export interface MijnIndelingHalfProps {
  entry: MyAssignmentDagdeelEntry;
  holidayName: string | null;
  dagLabel: string;
  dagdeelLabel: 'ochtend' | 'middag';
}

/** Eén dagdeel-helft — `role="gridcell"`, geen `<button>` (read-only, geen klik/hover-ring, C6). */
export function MijnIndelingHalf({ entry, holidayName, dagLabel, dagdeelLabel }: MijnIndelingHalfProps) {
  return (
    <div role="gridcell" aria-label={buildAriaLabel(dagLabel, dagdeelLabel, entry)} className="h-5 rounded-sm overflow-hidden border border-[#E2E8F0]">
      <MijnIndelingHalfContent entry={entry} holidayName={holidayName} />
    </div>
  );
}
